import { ValidatorFn } from "@angular/forms";

export interface QuestionBaseParams<T> {
  key: string;
  questions?: QuestionBase[];
  value?: T;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  hide?: boolean;
  order?: number;
  ignoreInSubmit?: boolean;
  callbacks?: Callbacks;
  copyToClipboard?: boolean;
  tooltipEnabled?: boolean;
  iconTooltipText?: string;
}

export interface FileInputOptions {
  multiple?: boolean;
  accept?: string;
}

interface ImageRequirements {
  fileRestrictions: {
    maxSize: number;
    maxWidth: number;
    maxHeight: number;
  };
  imageName: string;
  fileTooBig: boolean;
  invalidDimensions: boolean;
  inputNode?: HTMLInputElement;
}

export interface BaseQuestionDependsOn {
  key: string;
}

export interface Callbacks {
  onChange?(v?: any): void;
}

export class QuestionBase<T = string | boolean | number | unknown> implements QuestionBaseParams<T> {
  key: string;
  questions: QuestionBase[];
  value?: T;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  hide?: boolean;
  order?: number;
  ignoreInSubmit?: boolean;
  callbacks?: Callbacks;
  tooltipEnabled?: boolean;
  iconTooltipText?: string;
  // TODO: check props below
  placeholder: string;
  controlType: string;
  type: string;
  dependsOn?: BaseQuestionDependsOn;
  fileOptions?: FileInputOptions;
  errors: { key: string; validator: ValidatorFn; message: string }[];
  imageReqs: ImageRequirements;
  delete?: {
    show?: boolean;
    callback: () => void;
  };
  minDate: Date;
  maxDate: Date;
  isCurrency?: boolean;
  step: string;
  minValue: number;
  bindQueryParamsToForm = false;
  onlyShowUploadButton?: boolean;
  maxLength?: number;
  validated?: boolean;
  chipList?: {
    bind?: boolean;
    uniqueKey?: string[];
    labelKeys?: { key: string; format?: any }[];
  } = { bind: false, uniqueKey: ["id"], labelKeys: [] };
  link?: string;
  hidePassword?: boolean;
  minHeight?: number;
  copyToClipboard?: boolean;

  constructor(
    options: QuestionBaseParams<T> & {
      placeholder?: string;
      type?: string;
      dependsOn?: BaseQuestionDependsOn;
      fileOptions?: FileInputOptions;
      errors?: { key: string; validator: ValidatorFn; message: string }[];
      imageReqs?: ImageRequirements;
      delete?: {
        show: boolean;
        callback: () => void;
      };
      minDate?: Date;
      maxDate?: Date;
      isCurrency?: boolean;
      step?: string;
      minValue?: number;
      bindQueryParamsToForm?: boolean;
      maxLength?: number;
      onlyShowUploadButton?: boolean;
      chipList?: {
        bind?: boolean;
        uniqueKey?: string[];
        labelKeys?: { key: string; format?: any }[];
      };
      link?: string;
      minHeight?: number;
      copyToClipboard?: boolean;
    },
  ) {
    this.key = options.key || "";
    this.value = options.value;
    this.label = options.label || "";
    this.required = options.required;
    this.disabled = options.disabled;
    this.hide = options.hide;
    this.order = options.order === undefined ? 1 : options.order;
    this.ignoreInSubmit = options.ignoreInSubmit ?? false;
    this.callbacks = options.callbacks;
    this.tooltipEnabled = options.tooltipEnabled;
    this.iconTooltipText = options.iconTooltipText;
    this.placeholder = options.placeholder || "";
    this.type = options.type || "";
    this.dependsOn = options.dependsOn;
    this.fileOptions = options.fileOptions || {};
    this.errors = options.errors || [];
    this.imageReqs = options.imageReqs;
    this.delete = options.delete;
    this.minDate = options.minDate;
    this.maxDate = options.maxDate;
    this.minValue = options.minValue;
    this.isCurrency = options.isCurrency;
    this.step = options.step;
    this.bindQueryParamsToForm = options.bindQueryParamsToForm;
    this.maxLength = options.maxLength;
    this.onlyShowUploadButton = options.onlyShowUploadButton;
    this.validated = false;
    this.chipList = {
      ...this.chipList,
      ...options.chipList,
    };
    this.link = options.link;
    if (this.type === "password") {
      this.hidePassword = true;
    }
    this.minHeight = options.minHeight;
    this.copyToClipboard = options.copyToClipboard;
  }
}
